import { useTranslation } from 'react-i18next';
import { PARTNER_MODELS } from '@/mocks/dealer';
import type { ApplicationType } from '@/types/domain';

const PARTNER_ICONS: Record<string, string> = {
  distributor: 'ri-truck-line',
  architect: 'ri-ruler-2-line',
  construction: 'ri-building-line',
  realtor: 'ri-home-4-line',
  other: 'ri-more-line',
};

interface PartnerModelsSectionProps {
  onApply: (type: ApplicationType | '') => void;
}

export default function PartnerModelsSection({ onApply }: PartnerModelsSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="bg-foreground-950 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-3xl text-center">
          <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
          <h2 className="font-heading text-3xl leading-tight text-background-50 md:text-4xl lg:text-5xl">
            {t('dealer.proTitle')}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-background-100/70 md:text-lg">
            {t('dealer.proSubtitle')}
          </p>
          <p className="mt-4 text-sm font-medium uppercase tracking-widest text-primary-400">
            {t('dealer.proSectors')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {PARTNER_MODELS.map((partner, index) => (
            <button
              key={partner.id}
              type="button"
              onClick={() => onApply(partner.applicationType)}
              className={`reveal flex flex-col items-center gap-4 rounded-lg border border-foreground-700/60 bg-foreground-900/50 px-5 py-7 text-center transition-colors hover:border-primary-500/60 cursor-pointer ${
                index === 1 ? 'reveal-delay-1' : index === 2 ? 'reveal-delay-2' : index === 3 ? 'reveal-delay-3' : ''
              }`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500/10 text-primary-400">
                <i className={`${PARTNER_ICONS[partner.id]} text-2xl`} aria-hidden="true"></i>
              </span>
              <span className="text-sm font-medium text-background-100">{t(partner.labelKey)}</span>
            </button>
          ))}
        </div>

        <div className="reveal reveal-delay-1 mt-10 flex flex-col items-center gap-5">
          <p className="max-w-2xl text-center text-sm leading-relaxed text-background-100/60">
            {t('dealer.partnersNote')}
          </p>
          <button
            type="button"
            onClick={() => onApply('other')}
            className="whitespace-nowrap rounded-md bg-primary-500 px-8 py-3.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 cursor-pointer"
          >
            {t('dealer.partnerCta')}
          </button>
        </div>
      </div>
    </section>
  );
}